import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

// Interface for storing image sets
export interface ImageSet {
  id: string;
  name: string;
  heroUrl: string;
  productUrls: string[];
  heroParams?: { [key: string]: any };
  productParams?: { [key: string]: any };
  heroEditorContent?: string;
  productEditorContent?: string;
}

interface ImageUrlEditorProps {
  defaultHeroUrl: string;
  defaultProductUrls: string[];
  onHeroUrlChange: (url: string) => void;
  onProductUrlsChange: (urls: string[]) => void;
  onSaveImageSet?: (imageSet: Omit<ImageSet, 'id'>) => void;
  onUpdateImageSet?: (imageSet: ImageSet) => void;
  imageSets?: ImageSet[];
  onSelectImageSet?: (imageSet: ImageSet) => void;
  onDeleteImageSet?: (imageSetId: string) => void;
  onExportImageSets?: () => void;
  onImportImageSets?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  currentImageSetId?: string | null; // Add this to track the currently selected set
}

function ImageUrlEditor({ 
  defaultHeroUrl, 
  defaultProductUrls, 
  onHeroUrlChange, 
  onProductUrlsChange,
  onSaveImageSet,
  onUpdateImageSet,
  imageSets = [],
  onSelectImageSet,
  onDeleteImageSet,
  onExportImageSets,
  onImportImageSets,
  currentImageSetId = null
}: ImageUrlEditorProps) {
  const [heroUrl, setHeroUrl] = useState<string>(defaultHeroUrl);
  const [productUrlsText, setProductUrlsText] = useState<string>(defaultProductUrls.join('\n'));
  const [imageSetName, setImageSetName] = useState<string>('');
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const [editingSet, setEditingSet] = useState<ImageSet | null>(null);
  const heroTextareaRef = useRef<HTMLTextAreaElement>(null);
  const productTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useLanguage();

  // Auto-adjust height of hero textarea based on content
  useEffect(() => {
    if (heroTextareaRef.current) {
      heroTextareaRef.current.style.height = 'auto';
      const newHeight = heroTextareaRef.current.scrollHeight;
      heroTextareaRef.current.style.height = `${newHeight}px`;
    }
  }, [heroUrl]);

  // Auto-adjust height of product urls textarea based on content
  useEffect(() => {
    if (productTextareaRef.current) {
      productTextareaRef.current.style.height = 'auto';
      const newHeight = productTextareaRef.current.scrollHeight;
      productTextareaRef.current.style.height = `${newHeight}px`;
    }
  }, [productUrlsText]);

  const handleHeroUrlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newUrl = e.target.value;
    setHeroUrl(newUrl);
    onHeroUrlChange(newUrl);
  };

  const handleProductUrlsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setProductUrlsText(newText);
    
    // Split by new lines and filter out empty lines
    const urls = newText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);
      
    onProductUrlsChange(urls);
  };

  // Start editing an image set
  const handleEditSet = (set: ImageSet) => {
    setEditingSet(set);
    setImageSetName(set.name);
    setHeroUrl(set.heroUrl);
    setProductUrlsText(set.productUrls.join('\n'));
    setShowEditor(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSet(null);
    setImageSetName('');
    setHeroUrl(defaultHeroUrl);
    setProductUrlsText(defaultProductUrls.join('\n'));
    setShowEditor(false);
  };

  const handleSaveImageSet = () => {
    if (!imageSetName.trim()) return;
    
    // Split product URLs by new lines and filter out empty lines
    const productUrls = productUrlsText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);
    
    // If editing an existing set
    if (editingSet && onUpdateImageSet) {
      onUpdateImageSet({
        ...editingSet,
        name: imageSetName,
        heroUrl,
        productUrls
      });
      
      // Reset editing state
      setEditingSet(null);
      // Hide the editor
      setShowEditor(false);
    } 
    // Creating a new set
    else if (onSaveImageSet) {
      onSaveImageSet({
        name: imageSetName,
        heroUrl,
        productUrls
      });
    }
    
    // Clear the name field after saving
    setImageSetName('');
    
    // Hide the editor after saving for both new and edited sets
    setShowEditor(false);
  };

  return (
    <div className="bg-gray-800 p-4 rounded-lg mb-6">
      {/* Toggle Editor Button */}
      <div className="mb-4 flex justify-center">
        {!editingSet && (
          <button
            onClick={() => {
              if (showEditor) {
                setShowEditor(false);
                handleCancelEdit(); // Reset form when hiding
              } else {
                setShowEditor(true);
              }
            }}
            className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-700 text-white"
          >
            {showEditor ? t('hide_editor') : t('add_new_set')}
          </button>
        )}
      </div>
      
      {showEditor && (
        <>
          <div className="mb-4">
            <h4 className="text-white font-medium mb-2 border-b border-gray-700 pb-1">
              {t('hero_image_url')}
            </h4>
            <textarea
              ref={heroTextareaRef}
              className="w-full min-h-[40px] bg-gray-900 text-gray-100 p-3 font-mono text-sm rounded resize-none"
              value={heroUrl}
              onChange={handleHeroUrlChange}
              placeholder="Enter hero image URL..."
              spellCheck={false}
            />
          </div>
          
          <div className="mb-4">
            <h4 className="text-white font-medium mb-2 border-b border-gray-700 pb-1">
              {t('product_image_urls')}
            </h4>
            <p className="text-gray-400 text-sm mb-2">
              {t('one_url_per_line')}
            </p>
            <textarea
              ref={productTextareaRef}
              className="w-full min-h-[200px] bg-gray-900 text-gray-100 p-3 font-mono text-sm rounded resize-none"
              value={productUrlsText}
              onChange={handleProductUrlsChange}
              placeholder="Enter product image URLs (one per line)..."
              spellCheck={false}
            />
          </div>
        </>
      )}
      
      {/* Save/Update Image Set Section */}
      {showEditor && (
        <div className="mb-6 border-t border-gray-700 pt-4">
          <h4 className="text-white font-medium mb-2">
            {editingSet ? t('update_image_set') : t('save_image_set')}
          </h4>
          <div className="flex space-x-2 mb-4">
            <input
              type="text"
              value={imageSetName}
              onChange={(e) => setImageSetName(e.target.value)}
              placeholder={t('enter_image_set_name')}
              className="flex-1 bg-gray-900 text-gray-100 p-2 rounded border border-gray-700"
            />
            <button
              onClick={handleSaveImageSet}
              disabled={!imageSetName.trim()}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded"
            >
              {editingSet ? t('update_image_set') : t('save_image_set')}
            </button>
            
            {/* Show cancel button when editing */}
            {editingSet && (
              <button
                onClick={handleCancelEdit}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
              >
                {t('cancel_edit')}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Saved Image Sets Section */}
      {onSelectImageSet && (
        <div className="mb-6 border-t border-gray-700 pt-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-white font-medium">
              {t('saved_image_sets')}
            </h4>
            
            {/* Import/Export Buttons */}
            <div className="flex space-x-2">
              {onExportImageSets && imageSets.length > 0 && (
                <button
                  onClick={onExportImageSets}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                >
                  {t('export_sets')}
                </button>
              )}
              
              {onImportImageSets && (
                <div className="relative">
                  <button
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                    onClick={() => document.getElementById('import-sets-input')?.click()}
                  >
                    {t('import_sets')}
                  </button>
                  <input
                    id="import-sets-input"
                    type="file"
                    accept=".json"
                    onChange={onImportImageSets}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>
          
          {imageSets.length > 0 ? (
            <div className="space-y-2 h-full overflow-y-auto">
              {imageSets.map((set) => {
                // Check if this is the currently selected image set
                const isSelected = set.id === currentImageSetId;
                
                return (
                  <div 
                    key={set.id} 
                    className={`flex justify-between items-center p-2 rounded relative ${
                      isSelected ? 'bg-gray-700 border border-blue-400' : 'bg-gray-700'
                    }`}
                  >
                    <span className="truncate flex-1 text-white">
                      {set.name}
                      {set.id === 'original' && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-200">
                          {t('default')}
                        </span>
                      )}
                    </span>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onSelectImageSet(set)}
                        className={`text-white px-3 py-1 rounded text-sm ${
                          isSelected 
                            ? 'bg-blue-600 hover:bg-blue-700 border border-blue-300' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        disabled={isSelected}
                        title={isSelected ? 'Currently selected' : 'Select this image set'}
                      >
                        {isSelected ? t('selected') : t('select_image_set')}
                      </button>
                      {set.id !== 'original' && (
                        <button
                          onClick={() => handleEditSet(set)}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                        >
                          {t('edit_image_set')}
                        </button>
                      )}
                      {onDeleteImageSet && set.id !== 'original' && (
                        <button
                          onClick={() => onDeleteImageSet(set.id)}
                          className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-1 rounded text-sm"
                        >
                          {t('delete_image_set')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-400 text-center py-3">
              {t('no_saved_sets')}
            </div>
          )}
        </div>
      )}
      
      
    </div>
  );
}

export default ImageUrlEditor;